import { PartnerLogo } from "@components/brand/PartnerLogo";
import { PARTNERS } from "@/lib/partners";
import { FLEX } from "@/lib/site";
import { cn } from "@/ui/utils/cn";
import { track } from "@services/analytics";
import { useTranslations } from "@services/strings";
import { ArrowRight, ChevronRight } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";

/**
 * The bridge from Unlock to MNZL Flex: a navy band that names the partner
 * developers and sends their homeowners to the co-branded landing.
 */
export const FlexStrip = ({
  title,
  body,
  cta,
  className,
  placement,
}: {
  title: string;
  body: string;
  cta: string;
  className?: string;
  placement: string;
}) => {
  const { t } = useTranslations();
  const { locale } = useRouter();
  const lang = locale === "ar-EG" ? "ar" : "en";

  if (!FLEX.live) {
    return null;
  }

  return (
    <section className={cn("bg-white py-6 md:py-10", className)}>
      <div className="container">
        <div className="relative overflow-hidden rounded-4xl bg-navy px-6 py-10 text-white md:px-12 md:py-14">
          <div className="grid gap-8 md:grid-cols-[1.2fr_0.8fr] md:items-center">
            <div className="max-w-xl">
              <h2 className="display text-2xl md:text-3xl">{title}</h2>
              <p className="mt-4 text-body text-slate-300">{body}</p>
              <Link
                href="/flex"
                onClick={() =>
                  track({
                    event: "Button Clicked",
                    properties: { label: `Flex strip - ${placement}` },
                  })
                }
                className="group mt-6 inline-flex h-11 items-center gap-2 rounded-full bg-mint px-5 text-[15px] font-semibold text-ink transition-colors hover:bg-mint-soft"
              >
                {cta}
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1"
                  aria-hidden="true"
                />
              </Link>
            </div>
            <ul className="flex flex-col gap-3 md:justify-self-end">
              {PARTNERS.map((partner) => (
                <li key={partner.slug}>
                  <Link
                    href={`/flex/${partner.slug}`}
                    className="flex items-center gap-4 rounded-2xl border border-white/15 bg-white/5 p-3 pe-5 transition-colors hover:bg-white/10"
                  >
                    <span className="grid h-12 w-24 shrink-0 place-items-center rounded-xl bg-white px-3 sm:w-32">
                      <PartnerLogo
                        partner={partner}
                        lang={lang}
                        className="h-auto max-h-5 w-auto max-w-full"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-semibold">
                        {partner.name[lang]}
                      </span>
                      <span className="block text-sm text-slate-300">
                        {partner.compounds[lang].join(" · ")}
                      </span>
                    </span>
                    {/* On a phone the row has no room for the words: a chevron says the same. */}
                    <span className="hidden shrink-0 text-sm text-mint sm:inline">
                      {t("site.common.learnMore")}
                    </span>
                    <ChevronRight
                      size={18}
                      className="shrink-0 text-mint rtl:rotate-180 sm:hidden"
                      aria-hidden="true"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
